import { Site } from '@ps/web-core-enums';
import { FeatureFlag } from '@ps/web-core-store';
import type { FormApi } from 'final-form';
import { omit } from 'lodash';
import React, { useEffect } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import Toggle from '@swf/components/Inputs/Toggle';
import RegulatoryCheckboxes from '@swf/components/RegulatoryCheckboxes/RegulatoryCheckboxes';
import Button from '@swf/components/StarsButton/StarsButton';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';

import AccountSecurityInfo from '../AccountSecurityInfo/AccountSecurityInfo';

import {
  TXTCLI_Continue,
  TXTCLI_Create_Account,
  TXTCLI_Select_All
} from './RegistrationEulaFields.messages.json';

export interface RegistrationEulaFormValues {
  marketingMailList: boolean;
  toggleAll: boolean;
  eula?: boolean;
  privacy?: boolean;
  combinedEulaPrivacy?: boolean;
  gamingAccAgreement?: boolean;
}

const toggleAll = (
  e: React.ChangeEvent<HTMLInputElement>,
  formApi: FormApi<RegistrationEulaFormValues>
) => {
  const valueKeys = Object.keys(formApi.getState().values);
  valueKeys.forEach((item) => {
    formApi.change(item as keyof RegistrationEulaFormValues, e.target.checked);
  });
};

const getIsToogleAll = (values: RegistrationEulaFormValues): boolean =>
  Object.values(omit(values, ['toggleAll'])).every((value) => value === true);

const RegistrationEulaFields = () => {
  const siteId = useSelector(select.cms.getSiteId);
  const loading = useSelector((rootState: RootState) => !!rootState.loading?.models.auth);
  const verificationRequired = useSelector(select.account.isAgeVerificationRequired);
  const disableNewUkAge = useFeature(FeatureFlag.DisableNewUkAgeVerificationFlow);
  const form = useForm<RegistrationEulaFormValues>();
  const { invalid, validating, values, pristine, submitting } =
    useFormState<RegistrationEulaFormValues>();

  const isToogleAll = getIsToogleAll(values);

  useEffect(() => form.change('toggleAll', isToogleAll), [form, isToogleAll]);

  return (
    <>
      <Toggle
        onOffToggle
        id="toggleAll"
        name="toggleAll"
        type="switch"
        onChange={(e) => toggleAll(e, form)}
        label={TXTCLI_Select_All}
      />
      <RegulatoryCheckboxes variant="stars" />
      {[Site.USPA, Site.USMI].includes(siteId) && <AccountSecurityInfo />}
      <ButtonGroup>
        <Button
          block
          type="submit"
          disabled={invalid || submitting || pristine}
          loading={loading || validating}
          translation={
            !disableNewUkAge && verificationRequired ? TXTCLI_Continue : TXTCLI_Create_Account
          }
        />
      </ButtonGroup>
    </>
  );
};

export default RegistrationEulaFields;
